/**
 * The home page. Brian: "I want the dashboard to be clean and user friendly."
 *
 * What a coordinator or instructor needs at a glance, in the order they need
 * it: what is happening today, what needs a decision, where the System stands.
 * Everything here is a summary of a page that exists — the numbers link to
 * the Tracker, the sessions to Run it, the unbound classes to their bind
 * panel. Nothing is computed here that the other pages do not already show.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/useAuth.js';
import { apiFetch } from '../lib/api.js';
import ErrorBanner from '../components/ErrorBanner.jsx';
import { SessionNextStep, SessionStageBadge } from '../components/SessionNextStep.jsx';
import { withLane } from '../lib/lane.js';

const WHEN = new Intl.DateTimeFormat('en-US', {
  weekday: 'short',
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  timeZone: 'America/New_York',
});
const when = (iso) => (iso ? WHEN.format(new Date(iso)) : '—');
const hours = (n) => `${Number(n).toFixed(Number(n) % 1 === 0 ? 0 : 1)} h`;

export function DashboardPage() {
  const { profile } = useAuth();
  const [tracker, setTracker] = useState(undefined);
  const [sessions, setSessions] = useState(undefined);
  const [sections, setSections] = useState([]);
  const [error, setError] = useState(null);

  const can = (p) => profile?.permissions?.includes(p) ?? false;

  useEffect(() => {
    let active = true;
    Promise.all([
      apiFetch(withLane('/api/v1/tracker')).catch(() => null),
      apiFetch('/api/v1/sessions').catch(() => null),
      apiFetch('/api/v1/classes').catch(() => []),
    ])
      .then(([t, s, c]) => {
        if (!active) return;
        setTracker(t);
        setSessions(s ?? []);
        setSections(c ?? []);
      })
      .catch((err) => active && setError(err));
    return () => {
      active = false;
    };
  }, []);

  const titleById = new Map(sections.map((c) => [c.id, c.title]));
  const now = Date.now();
  const soon = now + 7 * 86_400_000;
  const live = (sessions ?? []).filter((s) => s.status === 'active');
  const upcoming = (sessions ?? [])
    .filter((s) => s.status === 'scheduled' && Date.parse(s.scheduled_start) >= now - 3 * 3_600_000 && Date.parse(s.scheduled_start) <= soon)
    .sort((a, b) => Date.parse(a.scheduled_start) - Date.parse(b.scheduled_start));
  const unbound = (sessions ?? []).filter((s) => s.is_impromptu && s.class_version == null && s.status !== 'canceled');
  const recentClosed = (sessions ?? [])
    .filter((s) => s.status === 'closed' && Date.parse(s.closed_at ?? s.scheduled_end) >= now - 14 * 86_400_000)
    .sort((a, b) => Date.parse(b.closed_at ?? b.scheduled_end) - Date.parse(a.closed_at ?? a.scheduled_end))
    .slice(0, 5);

  const firstName = profile?.fullName?.split(' ')[0] ?? 'there';

  return (
    <>
      <div className="page-head">
        <h1>Good {new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 18 ? 'afternoon' : 'evening'}, {firstName}</h1>
      </div>
      <ErrorBanner error={error} />

      {/* --- where the System stands ------------------------------------ */}
      {tracker && (
        <div className="tracker__totals home__totals">
          <Link className="card tracker__stat home__stat" to="/tracker">
            <span className="tracker__stat-number">{tracker.totals.people}</span>
            <span className="muted">people on record</span>
          </Link>
          <Link className="card tracker__stat home__stat" to="/tracker">
            <span className="tracker__stat-number">{hours(tracker.totals.hours)}</span>
            <span className="muted">earned</span>
          </Link>
          <Link className={`card tracker__stat home__stat ${tracker.totals.expired ? 'home__stat--warn' : ''}`} to="/tracker">
            <span className="tracker__stat-number">{tracker.totals.expired}</span>
            <span className="muted">people with something expired</span>
          </Link>
          <Link className={`card tracker__stat home__stat ${tracker.totals.due_soon ? 'home__stat--due' : ''}`} to="/tracker">
            <span className="tracker__stat-number">{tracker.totals.due_soon}</span>
            <span className="muted">due in the next {tracker.due_soon_days} days</span>
          </Link>
        </div>
      )}

      <div className="home__grid">
        {/* --- today ------------------------------------------------------ */}
        <section className="panel" aria-labelledby="today-heading">
          <h2 id="today-heading">Today</h2>
          {sessions === undefined && (
            <p className="muted" aria-busy="true">
              Loading…
            </p>
          )}
          {sessions && live.length === 0 && upcoming.length === 0 && (
            <p className="muted">Nothing open and nothing scheduled in the next seven days.</p>
          )}
          {live.length > 0 && (
            <ul className="home__list">
              {live.map((s) => (
                <li key={s.id} className="home__item">
                  <div>
                    <span className="badge badge--published">Open now</span>{' '}
                    <strong>{titleById.get(s.class_id) ?? 'Class'}</strong>
                    <div className="muted">
                      {s.location || 'No location'} · since {when(s.opened_at ?? s.scheduled_start)}
                    </div>
                  </div>
                  <Link className="btn btn--primary btn--auto" to={`/sessions/${s.id}/roster`}>
                    Run it
                  </Link>
                </li>
              ))}
            </ul>
          )}
          {upcoming.length > 0 && (
            <ul className="home__list">
              {upcoming.map((s) => (
                <li key={s.id} className="home__item">
                  <div>
                    <strong>{titleById.get(s.class_id) ?? 'Class'}</strong>
                    <div className="muted">
                      {when(s.scheduled_start)} · {s.location || 'No location'}
                    </div>
                  </div>
                  <Link className="btn btn--auto" to={`/sessions/${s.id}/roster`}>
                    Roster
                  </Link>
                </li>
              ))}
            </ul>
          )}
          <p className="toolbar">
            <Link className="btn btn--quiet btn--auto" to="/sessions">
              All sessions
            </Link>
            {can('session.impromptu') && (
              <Link className="btn btn--quiet btn--auto" to="/sessions">
                Start a class now
              </Link>
            )}
          </p>
        </section>

        {/* --- needs a decision ------------------------------------------- */}
        <section className="panel" aria-labelledby="attention-heading">
          <h2 id="attention-heading">Needs attention</h2>
          {sessions && unbound.length === 0 && (!tracker || tracker.totals.expired === 0) && (
            <p className="muted">Nothing waiting on you.</p>
          )}
          {unbound.length > 0 && (
            <ul className="home__list">
              {unbound.map((s) => (
                <li key={s.id} className="home__item">
                  <div>
                    <span className="badge badge--warn">No section yet</span>{' '}
                    <strong>{titleById.get(s.class_id) ?? 'Impromptu class'}</strong>
                    <div className="muted">
                      {when(s.scheduled_start)} · {s.status === 'closed' ? 'closed, waiting to be bound' : s.status}
                    </div>
                  </div>
                  <Link className="btn btn--auto" to={`/sessions/${s.id}/roster`}>
                    {can('session.manage') ? 'Bind it' : 'Open'}
                  </Link>
                </li>
              ))}
            </ul>
          )}
          {tracker && tracker.totals.expired > 0 && (
            <p>
              <Link to="/tracker">
                {tracker.totals.expired} {tracker.totals.expired === 1 ? 'person has' : 'people have'} something expired
              </Link>
              {tracker.courses?.length > 0 && (
                <span className="muted">
                  {' '}
                  — {tracker.courses
                    .filter((c) => c.expired > 0)
                    .map((c) => `${c.code} ${c.expired}`)
                    .join(', ')}
                </span>
              )}
            </p>
          )}
          {recentClosed.length > 0 && (
            <>
              <h3 className="home__h3">Recently closed</h3>
              <ul className="home__list home__list--quiet">
                {recentClosed.map((s) => (
                  <li key={s.id} className="home__item">
                    <div>
                      <strong>{titleById.get(s.class_id) ?? 'Class'}</strong>
                      <div className="muted">{when(s.scheduled_start)} · <SessionStageBadge session={s} /></div>
                    </div>
                    <SessionNextStep session={s} compact />
                  </li>
                ))}
              </ul>
            </>
          )}
        </section>
      </div>

      {/* --- who you are ------------------------------------------------- */}
      <section className="panel panel--quiet" aria-labelledby="account-heading">
        <h2 id="account-heading">You</h2>
        <p>
          <strong>{profile?.fullName}</strong>
          {profile?.institution && <> · {profile.institution.name}</>}
          {profile?.roles?.length > 0 && <span className="muted"> · {profile.roles.map((r) => r.name).join(', ')}</span>}
        </p>
        <p className="muted">
          Everything on this page comes from the database as of just now. A class runs as: build the
          section → open the class → the room checks in → verify, close, certify, award → the Tracker
          updates. Participants need no account; their own record is on their phone at <code>/me</code>.
        </p>
      </section>
    </>
  );
}

export default DashboardPage;
